import React, { useState } from "react";
import { Modal, Button, Row, Col, Spinner, Image } from "react-bootstrap";
import CollegeImagesService from "../../Services/CollegeImagesService";
import { toast } from "react-hot-toast";

function AddCollegeImage({
  showModal,
  handleCloseModal,
  collegeId,
  onSuccess,
}) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((file) => {
      const isValidType = file.type.startsWith("image/");
      const isValidSize = file.size <= 5 * 1024 * 1024;

      if (!isValidType) {
        toast.error(`${file.name} is not a valid image file`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedImages((prev) => [...prev, ...validFiles]);

      // Create preview images using base64 encoding (Success-360 pattern)
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(",")[1];
          setPreviewImages((prev) => [
            ...prev,
            {
              id: `preview-${Date.now()}-${Math.random()}`,
              name: file.name,
              type: file.type,
              base64: base64String,
              size: file.size,
            },
          ]);
        };
        reader.readAsDataURL(file);
      });

      toast.success(`${validFiles.length} image(s) added successfully`);
    }
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedImages.length === 0) {
      toast.error("Please select at least one image");
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      selectedImages.forEach((image) => {
        formData.append("image", image);
      });

      const response = await CollegeImagesService.createCollegeImages(
        formData,
        collegeId
      );

      if (response.data.success) {
        toast.success("College images uploaded successfully");
        setSelectedImages([]);
        handleCloseModal();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data.message || "Failed to upload images");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload images");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedImages([]);
    setPreviewImages([]);
    setDragActive(false);
    handleCloseModal();
  };

  return (
    <Modal show={showModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add College Images</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Drag and Drop Area */}
        <div
          className={`border-2 border-dashed rounded p-4 text-center ${
            dragActive ? "border-primary bg-light" : "border-secondary"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => document.getElementById("fileInput").click()}
        >
          <i
            className="bi bi-cloud-upload"
            style={{ fontSize: "3rem", color: "#6c757d", marginBottom: "1rem" }}
          ></i>
          <h5 className="text-muted">Drag & Drop images here</h5>
          <p className="text-muted mb-3">or click to browse files</p>
          <Button variant="outline-primary" size="sm">
            Choose Files
          </Button>
          <input
            id="fileInput"
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: "none" }}
          />
          <small className="text-muted mt-2">
            Supported formats: JPG, PNG, GIF, WebP (Max 5MB each)
          </small>
        </div>

        {/* Selected Images Preview - Success-360 Pattern */}
        {previewImages.length > 0 && (
          <div className="mt-4">
            <h6>Selected Images ({previewImages.length})</h6>
            <Row className="g-2">
              {previewImages.map((preview, index) => (
                <Col xs={6} md={4} key={preview.id}>
                  <div className="position-relative">
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "4/3",
                        overflow: "hidden",
                        borderRadius: "8px",
                        border: "1px solid #dee2e6",
                        background: "#f8f9fa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Image
                        src={`data:${preview.type};base64,${preview.base64}`}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute"
                      style={{ top: "5px", right: "5px" }}
                      onClick={() => removeImage(index)}
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                    <small className="text-muted d-block mt-1 text-truncate">
                      {preview.name}
                    </small>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={isSaving || previewImages.length === 0}
        >
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading...
            </>
          ) : (
            `Upload ${previewImages.length} Image${
              previewImages.length !== 1 ? "s" : ""
            }`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddCollegeImage;
